/**
 * Badge Component
 *
 * A small badge component for displaying short status text or labels.
 * Available in two sizes (sm, lg) and multiple visual variants.
 *
 * Features:
 * - Two size variants (sm, lg)
 * - Multiple visual variants (default, on-dark)
 * - Consistent styling with design system variables
 * - Uppercase text by default
 *
 * @example
 * ```tsx
 * <Badge label="NEW" size="sm" />
 * <Badge label="SUGGESTED REMOVAL" size="sm" />
 * <Badge label="BETA" variant="on-dark" />
 * ```
 */
import "./Badge.css";

/**
 * Size variants for the Badge component
 */
export type BadgeSize = "sm" | "lg";

/**
 * Visual variants for the Badge component
 */
export type BadgeVariant =
  | "default"
  | "on-dark"
  | "olive"
  | "amber"
  | "rose"
  | "sky";

/**
 * Props for the Badge component
 */
export interface BadgeProps {
  /**
   * Text content rendered inside the badge
   */
  label: string;

  /**
   * Size variant of the badge
   * - "sm": Small size (9px text, compact padding)
   * - "lg": Large size (standard body text, larger padding)
   * @default "sm"
   */
  size?: BadgeSize;

  /**
   * Visual variant of the badge
   * - "default": Subtle neutral background
   * - "on-dark": High-contrast option for dark surfaces
   * - "olive": Olive green background (Olive 500)
   * - "amber": Amber/golden background (Amber 300)
   * - "rose": Rose/red background (Rose 400)
   * - "sky": Sky blue background (Sky 400)
   * @default "default"
   */
  variant?: BadgeVariant;

  /**
   * Additional CSS classes to apply to the badge
   */
  className?: string;

  /**
   * Whether the label is transformed to uppercase.
   * @default true
   */
  uppercase?: boolean;
}

/**
 * Badge Component
 *
 * Displays a styled badge with customizable text.
 * The component uses design system variables for consistent styling.
 *
 * @param props - Component props (@see BadgeProps)
 * @returns A styled badge component
 */
export const Badge = ({
  label,
  size = "sm",
  variant = "default",
  className = "",
  uppercase = true,
}: BadgeProps) => {
  const classes = [
    "indigo-badge",
    `indigo-badge--${size}`,
    `indigo-badge--variant-${variant}`,
    !uppercase && "indigo-badge--no-uppercase",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes}>{uppercase ? label.toUpperCase() : label}</span>
  );
};
